import React, { useMemo } from 'react';
import { Box, Typography, alpha, Tooltip, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ShowChart, Assessment, Speed, Thermostat, Category,
  TrendingUp, ElectricBolt,
} from '@mui/icons-material';

const KpiCell = ({ icon: Icon, label, value, sub, color = '#3b82f6', tooltip }) => {
  const content = (
    <Box sx={{
      flex: '1 1 110px',
      display: 'flex', alignItems: 'center', gap: 1,
      px: 1.5, py: 1,
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1.5,
        bgcolor: alpha(color, 0.1),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {Icon && <Icon sx={{ fontSize: 16, color }} />}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.48rem', color: '#94a3b8', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1rem', fontWeight: 900, color, lineHeight: 1.1, fontFamily: 'monospace' }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', lineHeight: 1.2, mt: 0.1 }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return tooltip ? <Tooltip title={tooltip} arrow>{content}</Tooltip> : content;
};

const Divider = () => (
  <Box sx={{ width: '1px', bgcolor: '#e5e7eb', my: 0.5, flexShrink: 0 }} />
);

const KpiStrip = ({ watchlist = [], overheatSummary = {} }) => {
  const { t } = useTranslation();
  const stats = useMemo(() => {
    if (!watchlist.length) return null;

    const scores = watchlist.map(w => w.score || 0);
    const rrs = watchlist.map(w => w.rr || 0);
    const oh = watchlist.map(w => w.overheat_score || 0);
    const swingCount = watchlist.filter(w => w.trade_type === 'SWING').length;
    const tplusCount = watchlist.filter(w => w.trade_type === 'TPLUS').length;
    const breakoutCount = watchlist.filter(w => w.breakout).length;
    const contractionCount = watchlist.filter(w => w.tight_contraction).length;

    // Sector breakdown
    const sectors = {};
    watchlist.forEach(w => {
      const s = w.sector_code || 'N/A';
      sectors[s] = (sectors[s] || 0) + 1;
    });
    const topSector = Object.entries(sectors).sort((a, b) => b[1] - a[1]);

    // Avg ret
    const avgRet5d = watchlist.reduce((a, w) => a + (w.ret_5d || 0), 0) / watchlist.length;
    const avgRet20d = watchlist.reduce((a, w) => a + (w.ret_20d || 0), 0) / watchlist.length;

    return {
      avgScore: (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2),
      avgRR: (rrs.reduce((a, b) => a + b, 0) / rrs.length).toFixed(1),
      avgOH: (oh.reduce((a, b) => a + b, 0) / oh.length).toFixed(0),
      swingCount,
      tplusCount,
      breakoutCount,
      contractionCount,
      sectorCount: Object.keys(sectors).length,
      topSector: topSector.length > 0 ? topSector[0] : null,
      avgRet5d,
      avgRet20d,
    };
  }, [watchlist]);

  if (!stats) return null;

  const ohColor = stats.avgOH >= 40 ? '#ef4444' : stats.avgOH >= 20 ? '#f59e0b' : '#10b981';
  const retColor = v => v > 0 ? '#10b981' : '#ef4444';

  return (
    <Paper elevation={0} sx={{
      border: '1px solid #e5e7eb', borderRadius: 2, mb: 2,
      display: 'flex', flexWrap: 'wrap', alignItems: 'stretch',
    }}>
      <KpiCell
        icon={Assessment}
        label="Avg Score"
        value={stats.avgScore}
        color="#3b82f6"
        tooltip={t('regimeWatchlist.kpi.avgScoreTooltip', 'Average score of all stocks in watchlist')}
      />
      <Divider />
      <KpiCell
        icon={Speed}
        label="Avg R:R"
        value={`${stats.avgRR}x`}
        sub={stats.avgRR >= 1.8 ? t('regimeWatchlist.kpi.good', 'Good') : t('regimeWatchlist.kpi.average', 'Average')}
        color={stats.avgRR >= 1.8 ? '#10b981' : '#f59e0b'}
        tooltip={t('regimeWatchlist.kpi.avgRRTooltip', 'Average Reward:Risk ratio. ≥1.8 is good.')}
      />
      <Divider />
      <KpiCell
        icon={Thermostat}
        label="Avg Overheat"
        value={`${stats.avgOH}`}
        sub={overheatSummary.safety_level || ''}
        color={ohColor}
        tooltip={t('regimeWatchlist.kpi.overheatTooltip', 'Average overheat score (0-100). Lower is safer.')}
      />
      <Divider />
      <KpiCell
        icon={ShowChart}
        label="SWING / T+"
        value={`${stats.swingCount} / ${stats.tplusCount}`}
        color="#8b5cf6"
        tooltip={`${stats.swingCount} ${t('regimeWatchlist.kpi.swingStocks', 'SWING stocks (10-30 sessions)')}, ${stats.tplusCount} ${t('regimeWatchlist.kpi.tplusStocks', 'T+ stocks (3-7 sessions)')}`}
      />
      <Divider />
      <KpiCell
        icon={ElectricBolt}
        label="Breakout"
        value={stats.breakoutCount}
        sub={`${t('regimeWatchlist.kpi.contraction', 'Contraction')}: ${stats.contractionCount}`}
        color="#f97316"
        tooltip={`${stats.breakoutCount} ${t('regimeWatchlist.kpi.breakoutTickers', 'tickers in breakout')}, ${stats.contractionCount} ${t('regimeWatchlist.kpi.contractionTickers', 'tickers in contraction (potential setup)')}`}
      />
      <Divider />
      <KpiCell
        icon={Category}
        label={t('regimeWatchlist.kpi.sectors', 'Sectors')}
        value={stats.sectorCount}
        sub={stats.topSector ? `Top: ${stats.topSector[0]}` : ''}
        color="#6366f1"
        tooltip={`${t('regimeWatchlist.kpi.sectorAllocation', 'Allocation across')} ${stats.sectorCount} ${t('regimeWatchlist.kpi.sectorsLower', 'sectors')}. ${t('regimeWatchlist.kpi.topSector', 'Top sector')}: ${stats.topSector?.[0]} (${stats.topSector?.[1]} ${t('regimeWatchlist.kpi.tickers', 'tickers')})`}
      />
      <Divider />
      <KpiCell
        icon={TrendingUp}
        label="Avg Ret"
        value={`${stats.avgRet5d >= 0 ? '+' : ''}${stats.avgRet5d.toFixed(1)}%`}
        sub={`20d: ${stats.avgRet20d >= 0 ? '+' : ''}${stats.avgRet20d.toFixed(1)}%`}
        color={retColor(stats.avgRet5d)}
        tooltip={`${t('regimeWatchlist.kpi.avgRet5d', 'Avg 5-session return')}: ${stats.avgRet5d.toFixed(1)}%, ${t('regimeWatchlist.kpi.avgRet20d', '20-session')}: ${stats.avgRet20d.toFixed(1)}%`}
      />
    </Paper>
  );
};

export default KpiStrip;
